import axios from "axios";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit2,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { toast } from "sonner";
import Pagination from "../../ui/pagination";
import { InputFieldset } from "../../ui/inputFieldset";
import {
  getEmployeeGrades,
  EmployeeGradeApi,
  createEmployeeGrade,
  updateEmployeeGrade,
  deleteEmployeeGrade,
} from "../../services/employeeGradeService";
import { FullScreenLoading } from "../../components/ui/loader";

interface EmployeeGrade {
  id: string;
  title: string;
  grade: string;
  level: number;
  description: string;
  status: "active" | "inactive";
}

export function EmployeeGradesPage() {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState<EmployeeGrade | null>(null);
  const [employeeGrades, setEmployeeGrades] = useState<EmployeeGrade[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<EmployeeGrade | null>(null);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formData, setFormData] = useState({
    title: "",
    grade: "",
    level: 1,
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    title: "",
    grade: "",
    level: "",
  });

  const fetchEmployeeGrades = async () => {
    try {
      setLoading(true);
      const search = searchTerm.trim();
      const isLevelSearch = search !== "" && /^\d+$/.test(search);
      const response = await getEmployeeGrades({
        gradeName: search !== "" && !isLevelSearch ? search : undefined,
        gradeLevel: isLevelSearch ? Number(search) : undefined,
        pageNo,
        pageSize,
      });

      const mappedData: EmployeeGrade[] = response.data.data.map(
        (item: EmployeeGradeApi) => ({
          id: item.publicId,
          title: item.description,
          grade: item.gradeName,
          level: item.gradeLevel,
          description: item.description,
          status: item.isActive ? "active" : "inactive",
        }),
      );

      setEmployeeGrades(mappedData);
      setTotalCount(response.data.totalRecords);
    } catch (error) {
      toast.error("Failed to load employee grades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageHeader({
      title: "Employee Grade",
      icon: <Users className="h-5 w-5" />,
    });

    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    fetchEmployeeGrades();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn("");
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    return [...employeeGrades].sort((a, b) => {
      if (!sortColumn) return 0;
      const aValue = a[sortColumn as keyof EmployeeGrade];
      const bValue = b[sortColumn as keyof EmployeeGrade];
      if (typeof aValue === "string" && typeof bValue === "string") {
        const result = aValue.toLowerCase().localeCompare(bValue.toLowerCase());

        return sortDirection === "asc" ? result : -result;
      }
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [employeeGrades, sortColumn, sortDirection]);

  const resetForm = () => {
    setFormData({
      title: "",
      grade: "",
      level: 1,
      status: "active",
    });

    setErrors({
      title: "",
      grade: "",
      level: "",
    });

    setEditingItem(null);
    setIsEditMode(false);
  };

  const handleOpenForm = (item?: EmployeeGrade) => {
    if (item) {
      setIsEditMode(true);
      setEditingItem(item);
      setFormData({
        title: item.title,
        grade: item.grade,
        level: item.level,
        status: item.status,
      });
    } else {
      setIsEditMode(false);
      setEditingItem(null);
      setFormData({
        title: "",
        grade: "",
        level: 1,
        status: "active",
      });
    }
    setErrors({
      title: "",
      grade: "",
      level: "",
    });
    setIsAddDialogOpen(true);
  };

  const handleSaveEmployeeGrade = async () => {
    const newErrors = {
      title: "",
      grade: "",
      level: "",
    };
    if (!formData.title.trim()) {
      newErrors.title = "Grade Title is required";
    } else if (formData.title.trim().length > 50) {
      newErrors.title = "Grade Title can contain maximum 50 characters.";
    }
    if (!formData.grade.trim()) {
      newErrors.grade = "Grade is required";
    } else if (!/^[A-Za-z]+$/.test(formData.grade.trim())) {
      newErrors.grade = "Grade can contain only letters.";
    } else if (formData.grade.trim().length > 10) {
      newErrors.grade = "Grade can contain maximum 10 characters.";
    }
    if (
      formData.level === undefined ||
      formData.level === null ||
      Number.isNaN(Number(formData.level))
    ) {
      newErrors.level = "Level is required";
    } else if (Number(formData.level) <= 0) {
      newErrors.level = "Level must be greater than 0";
    } else if (!Number.isInteger(Number(formData.level))) {
      newErrors.level = "Level must be a whole number";
    }
    const gradeExists = employeeGrades.some(
      (item) =>
        item.grade.toLowerCase() === formData.grade.trim().toLowerCase() &&
        item.id !== editingItem?.id,
    );
    if (gradeExists) {
      newErrors.grade = "Grade already used for another employee grade.";
    }
    setErrors(newErrors);
    if (newErrors.title || newErrors.grade || newErrors.level) {
      return;
    }
    try {
      setSaving(true);
      const payload = {
        gradeName: formData.grade.trim().toUpperCase(),
        gradeLevel: Number(formData.level),
        description: formData.title.trim(),
        isActive: formData.status === "active",
      };
      if (isEditMode && editingItem) {
        const response = await updateEmployeeGrade(editingItem.id, payload);
        toast.success(
          response.message || "Employee grade updated successfully",
        );
      } else {
        const response = await createEmployeeGrade(payload);
        toast.success(
          response.message || "Employee grade created successfully",
        );
      }
      setIsAddDialogOpen(false);
      resetForm();
      await fetchEmployeeGrades();
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.message ||
              (isEditMode
                ? "Failed to update employee grade."
                : "Failed to create employee grade.")
          : error instanceof Error
            ? error.message
            : isEditMode
              ? "Failed to update employee grade."
              : "Failed to create employee grade.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (item: EmployeeGrade) => {
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;
    try {
      setDeleting(true);
      const response = await deleteEmployeeGrade(deletingItem.id);
      toast.success(response.message || "Employee grade deleted successfully.");
      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
      await fetchEmployeeGrades();
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.message || "Failed to delete employee grade."
          : error instanceof Error
            ? error.message
            : "Failed to delete employee grade.",
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {(loading || saving || deleting) &&
        createPortal(
          <div className="fixed inset-0 z-[100]">
            <FullScreenLoading
              title={
                deleting
                  ? "Deleting employee grade..."
                  : saving
                    ? "Saving employee grade..."
                    : "Loading employee grades..."
              }
            />
          </div>,
          document.body,
        )}
      <div className="space-y-6 pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="relative flex-1 max-w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by grade or level"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setPageNo(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-2 shrink-0">
            <Button
              onClick={() => handleOpenForm()}
              style={{
                background: "#5B9BD5",
              }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Employee Grade
            </Button>
          </div>
        </div>
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{
            marginTop: "10px",
            gap: "0px",
          }}
        >
          <CardContent
            style={{
              padding: "0px 0px 10px 0px",
            }}
          >
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow
                    style={{
                      backgroundColor: "#E3F2FD",
                    }}
                  >
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("title")}
                    >
                      <div className="flex items-center gap-1">
                        Grade Title
                        {sortColumn === "title" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("grade")}
                    >
                      <div className="flex items-center gap-1">
                        Grade
                        {sortColumn === "grade" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("level")}
                    >
                      <div className="flex items-center gap-1">
                        Level
                        {sortColumn === "level" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs text-center"
                    >
                      Status
                    </TableHead>
                    <TableHead
                      style={{
                        color: "#5B9BD5",
                      }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No employee grades found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedData.map((item) => (
                      <TableRow key={item.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {item.title}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-600">
                            {item.grade}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-2">
                          <span className="text-xs text-gray-600">
                            {item.level}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center">
                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleOpenForm(item)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(item)}
                              className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 p-4">
              {sortedData.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No employee grades found</p>
                </div>
              ) : (
                sortedData.map((item) => (
                  <Card
                    key={item.id}
                    className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors"
                  >
                    <CardContent
                      style={{
                        padding: "14px",
                      }}
                    >
                      {/* Grade Title + Grade */}
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {item.title}
                            </p>
                            <span className="text-xs text-gray-600">
                              Grade: {item.grade}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Level */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Level:</span>

                          <span className="text-sm text-gray-700">
                            {item.level}
                          </span>
                        </div>
                      </div>

                      {/* Status + Actions */}
                      <div className="flex items-center justify-between border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status:</span>

                          <Badge
                            variant="outline"
                            className={`${
                              item.status === "active"
                                ? "bg-green-100 text-green-700 border-green-200"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            } text-xs`}
                          >
                            {item.status === "active" ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenForm(item)}
                            className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                            title="Edit"
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>

                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(item)}
                            className="h-8 text-xs text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="employee grades"
          />
        </Card>
        <Dialog
          open={isAddDialogOpen}
          onOpenChange={(open) => {
            setIsAddDialogOpen(open);

            if (!open) {
              resetForm();
            }
          }}
        >
          <DialogContent className="sm:max-w-[600px] bg-white">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                {isEditMode ? "Edit Employee Grade" : "Add Employee Grade"}
              </DialogTitle>
              <DialogDescription>
                {isEditMode
                  ? "Update employee grade information"
                  : "Create a new employee grade entry"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Grade Title"
                    required
                    disabled={saving}
                    id="title"
                    value={formData.title}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^A-Za-z ]/g, "")
                        .slice(0, 50);
                      setFormData({
                        ...formData,
                        title: value,
                      });
                      setErrors({
                        ...errors,
                        title: "",
                      });
                    }}
                    placeholder="e.g., Senior Manager"
                    className={`text-sm ${
                      errors.title ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-xs">{errors.title}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <InputFieldset
                    label="Grade"
                    required
                    disabled={saving}
                    id="grade"
                    value={formData.grade}
                    onChange={(e) => {
                      const value = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 10);

                      setFormData({
                        ...formData,
                        grade: value,
                      });

                      setErrors({
                        ...errors,
                        grade: "",
                      });
                    }}
                    placeholder="e.g., A"
                    className={`text-sm ${
                      errors.grade ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />
                  {errors.grade && (
                    <p className="text-red-500 text-xs">{errors.grade}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <InputFieldset
                    label="Level"
                    required
                    disabled={saving}
                    id="level"
                    type="number"
                    min={1}
                    value={formData.level}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        level: value === "" ? 0 : Number(value),
                      });
                      setErrors({
                        ...errors,
                        level: "",
                      });
                    }}
                    placeholder="e.g., 1"
                    className={`text-sm ${
                      errors.level ? "border-red-500 focus:ring-red-500" : ""
                    }`}
                  />
                  {errors.level && (
                    <p className="text-red-500 text-xs">{errors.level}</p>
                  )}
                </div>
                <div className="min-w-0 flex flex-col justify-center gap-2 px-3 py-2">
                  <label
                    htmlFor="employee-grade-status"
                    className="text-sm font-medium text-gray-700"
                  >
                    Status
                  </label>

                  <div className="flex items-center gap-3">
                    <Switch
                      id="employee-grade-status"
                      disabled={saving}
                      checked={formData.status === "active"}
                      onCheckedChange={(checked) =>
                        setFormData((current) => ({
                          ...current,
                          status: checked ? "active" : "inactive",
                        }))
                      }
                    />

                    <span className="text-sm text-gray-700">
                      {formData.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsAddDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEmployeeGrade}
                disabled={saving}
                style={{
                  background: "#5B9BD5",
                }}
                className="text-white"
              >
                {isEditMode ? "Update Employee Grade" : "Add Employee Grade"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Employee Grade
              </DialogTitle>

              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">"{deletingItem?.title}"</span>?
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                disabled={saving}
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
